import { Languages, LoaderCircle, X } from 'lucide-react';
import { memo, useCallback } from 'react';

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
} from '@/components/shadcn/ui/select';
import { cn } from '@/lib/cn';

import { TARGET_LANGUAGES, type TargetLanguage } from '../types/translation';

interface TranslateLanguageSelectProps {
  value: TargetLanguage | null;
  onChange: (value: TargetLanguage | null) => void;
  isLoading?: boolean;
  disabled?: boolean;
  className?: string;
}

const TranslateLanguageSelect = memo(
  ({
    value,
    onChange,
    isLoading = false,
    disabled = false,
    className,
  }: TranslateLanguageSelectProps) => {
    const selectedLanguage = TARGET_LANGUAGES.find((l) => l.value === value);

    const handleClear = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        onChange(null);
      },
      [onChange],
    );

    return (
      <div
        className={cn('relative inline-flex items-center gap-1.5', className)}
      >
        <Select
          value={value ?? ''}
          onValueChange={(v) => onChange(v as TargetLanguage)}
          disabled={disabled || isLoading}
        >
          <SelectTrigger size="sm">
            {isLoading ? (
              <div className="flex items-center gap-1.5">
                <LoaderCircle className="size-4 animate-spin" />
                <span>Translating...</span>
              </div>
            ) : (
              <div className="flex items-center gap-1.5">
                <Languages
                  className={cn(
                    'size-3.5',
                    value ? 'text-primary' : 'text-gray-600',
                  )}
                />
                <span>{selectedLanguage?.nativeLabel ?? 'Translate'}</span>
              </div>
            )}
          </SelectTrigger>
          <SelectContent align="end">
            <SelectGroup>
              {TARGET_LANGUAGES.map((language) => (
                <SelectItem key={language.value} value={language.value}>
                  <div className="flex w-full items-center justify-between gap-4">
                    <span className="font-medium">{language.nativeLabel}</span>
                    <span className="text-xs text-muted-foreground">
                      {language.label}
                    </span>
                  </div>
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>

        {value && !isLoading && (
          <button
            onClick={handleClear}
            className="flex size-4 cursor-pointer items-center justify-center rounded-full border-none bg-gray-200 transition-all hover:bg-gray-300"
          >
            <X className="size-2.5 text-gray-500" />
          </button>
        )}
      </div>
    );
  },
);

TranslateLanguageSelect.displayName = 'TranslateLanguageSelect';

export { TranslateLanguageSelect };
